import { Smartphone, Globe, Server, PenTool, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const Tag = ({ children }) => (
  <span className="font-mono text-[11px] uppercase tracking-wider text-teal-300/90 bg-teal-500/10 border border-teal-500/20 rounded-full px-3 py-1">
    {children}
  </span>
);

const Card = ({ icon: Icon, title, desc, tags, span, testid, delay }) => (
  <Reveal delay={delay} className={span}>
    <div
      data-testid={testid}
      className="group relative h-full bg-[#1A1B20] border border-zinc-800 rounded-2xl p-8 sm:p-10 overflow-hidden transition-all duration-500 hover:border-teal-500/50 hover:shadow-[0_0_50px_-12px_rgba(20,184,166,0.35)] hover:-translate-y-1.5"
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-teal-500/0 blur-3xl transition-all duration-700 group-hover:bg-teal-500/15" />
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/25 flex items-center justify-center text-teal-400 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6">
          <Icon size={22} />
        </div>
        <ArrowUpRight
          size={20}
          className="text-zinc-600 transition-all duration-500 group-hover:text-teal-400 group-hover:translate-x-1 group-hover:-translate-y-1"
        />
      </div>
      <h3 className="mt-8 font-display text-xl sm:text-2xl font-semibold tracking-tight text-zinc-50">
        {title}
      </h3>
      <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">{desc}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {tags.map((t) => (
          <Tag key={t}>{t}</Tag>
        ))}
      </div>
    </div>
  </Reveal>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-teal-400">
          01 — What we do
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-50 max-w-2xl">
          Software shaped around your idea — not the other way around.
        </h2>
      </Reveal>
      <Reveal delay={0.18}>
        <p className="mt-5 max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed">
          BTM Tech Developments designs and builds mobile apps and websites for founders, teams,
          and businesses. We take products from concept to launch — strategy, design, engineering,
          and everything in between.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
        <Card
          span="md:col-span-7"
          testid="service-card-mobile"
          icon={Smartphone}
          title="Mobile App Development"
          desc="Native-quality iOS and Android apps built with modern cross-platform tooling. From MVP to App Store launch — fast, reliable, and built to scale with your users."
          tags={["iOS", "Android", "React Native", "Offline-first", "App Store Launch"]}
          delay={0.05}
        />
        <Card
          span="md:col-span-5"
          testid="service-card-web"
          icon={Globe}
          title="Websites & Web Apps"
          desc="High-performance websites and web applications with modern React — beautiful, responsive, and optimized for speed and search."
          tags={["React", "Performance", "SEO", "Responsive"]}
          delay={0.12}
        />
        <Card
          span="md:col-span-5"
          testid="service-card-backend"
          icon={Server}
          title="Backend & API Engineering"
          desc="Robust APIs, databases, and cloud infrastructure that quietly power your product — secure, documented, and ready to grow."
          tags={["FastAPI", "Databases", "Cloud", "Integrations"]}
          delay={0.05}
        />
        <Card
          span="md:col-span-7"
          testid="service-card-design"
          icon={PenTool}
          title="UI/UX & Product Design"
          desc="Interfaces people understand instantly. We design systems, prototypes, and polished product experiences that make complex ideas feel simple."
          tags={["Design Systems", "Prototyping", "Brand-Aligned UI"]}
          delay={0.12}
        />
      </div>
    </div>
  </section>
);
